import { CourseFormFieldApi, CourseFromGroupApi } from "api";
import React, { useState } from "react";
import { useToaster } from "utils";
import { EmptyState } from "components/molecules";
import { ConfirmDeleteButton } from "components/molecules";
import { PaziaButton } from "components/atoms";
import { FIELD_CONDITIONS_TEXTS, FORM_INPUT_OPTIONS } from "consts";
import { AddApplicationFormFieldModal } from "components/templates/AddApplicationFormFieldModal/AddApplicationFormFieldModal";
import { FormFieldSchema } from "components/templates/AddApplicationFormGroupModal/ApplicationForm";
import { categoryFormGroupsApi } from "api/category-form-groups";
import { v4 as uuidv4 } from "uuid";

interface CategoryFormGroupDetailProps {
  categoryId: number;
  group: CourseFromGroupApi;
  allGroups: CourseFromGroupApi[];
  changeActiveGroup: (group: CourseFromGroupApi | null) => void;
  refreshGroups: () => void;
}

export const CategoryFormGroupDetail = ({
  categoryId,
  group,
  allGroups,
  changeActiveGroup,
  refreshGroups,
}: CategoryFormGroupDetailProps): JSX.Element => {
  const { showSuccess, showError } = useToaster();
  const [modalField, setModalField] = useState<CourseFormFieldApi | null>(null);

  const onFieldSubmit = async (formData: FormFieldSchema) => {
    try {
      if (formData.id) {
        await categoryFormGroupsApi.updateField(categoryId, group.courseFormGroupId, formData.id, formData);
        showSuccess("Pole zostało zaktualizowane.");
      } else {
        await categoryFormGroupsApi.addField(categoryId, group.courseFormGroupId, formData);
        showSuccess("Pole zostało dodane.");
      }
      refreshGroups();
      setModalField(null);
    } catch (e) {
      showError("Nie udało się dodać pola.");
    }
  };

  const onFieldDelete = async (fieldId: number) => {
    try {
      await categoryFormGroupsApi.deleteField(categoryId, group.courseFormGroupId, fieldId);
      showSuccess("Pole zostało usunięte.");
      refreshGroups();
    } catch (e) {
      showError("Nie udało się usunąć pola.");
    }
  };

  return (
    <>
      <div>
        <div
          className="cursor-pointer pb-4 text-sm font-semibold text-slate-700 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-200"
          onClick={() => changeActiveGroup(null)}
        >
          &lt;- Wróć do listy
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xl font-semibold">{group.name}</span>
            <div>
              <span className="text-sm italic text-slate-500">{group.description}</span>
            </div>
          </div>
        </div>
        {group.fields?.length > 0 ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full table-auto border-collapse border border-gray-200 dark:border-slate-700">
              <thead>
                <tr className="bg-gray-100 dark:bg-slate-900">
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Nazwa</th>
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Typ</th>
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Widoczność</th>
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {group.fields.map((field) => (
                  <tr key={field.id} className="hover:bg-gray-100 dark:hover:bg-slate-700">
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      <span className="font-semibold text-slate-800 dark:text-slate-100">{field.name}</span>
                      {field.isRequired && <span className="ml-1 text-red-500">*</span>}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      {FORM_INPUT_OPTIONS.find((x) => x.value == field.fieldType)?.label}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      {field.conditions && field.conditions.length > 0 ? (
                        <div className="text-sm text-yellow-600">
                          {field.conditions.map((con) => (
                            <div key={uuidv4()}>
                              Ukryte gdy{" "}
                              <span className="font-semibold">
                                {allGroups.flatMap((x) => x.fields).find((x) => x.id == con.fieldId)?.name}
                              </span>{" "}
                              {FIELD_CONDITIONS_TEXTS[con.condition]}{" "}
                              <span className="font-semibold">{con.conditionValue}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-green-600">Widoczny zawsze</div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      <div className="flex gap-2">
                        <ConfirmDeleteButton
                          title="Usuń"
                          dialog={{ title: "Usuń pole", description: "Czy na pewno chcesz usunąć pole?" }}
                          onConfirm={() => onFieldDelete(field.id)}
                          className="item-start"
                        />
                        <PaziaButton title="Edytuj" type="tertiary" onClick={() => setModalField(field)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-10">
            <EmptyState title="Pusta grupa" description="Dodaj pola formularza aby wypełnić grupę." />
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <PaziaButton
            title={group.fields?.length > 0 ? "Dodaj kolejne pole" : "Dodaj pole"}
            type="primary"
            onClick={() => setModalField({} as CourseFormFieldApi)}
          />
        </div>
      </div>
      <AddApplicationFormFieldModal
        field={modalField}
        onSubmit={onFieldSubmit}
        onCloseRequest={() => setModalField(null)}
        allFields={allGroups.flatMap((x) => x.fields)}
      />
    </>
  );
};
